/**
 * Groups an array of objects by a specified key.
 * @template T
 * @param {Array<T>} array - The array of objects to group.
 * @param {keyof T} key - The key to group by.
 * @returns {Record<string, Array<Omit<T, typeof key>>>} - An object with keys as group values and values as arrays of objects without the grouping key.
 */
export function groupBy(array, key) {
	return array.reduce(
		/**
		 * @param {Record<string, Array<Omit<T, typeof key>>>} result - The accumulator object that stores the grouped results.
		 * @param {T} currentObject - The current object being processed.
		 * @returns {Record<string, Array<Omit<T, typeof key>>>} - The updated accumulator object.
		 */
		function (result, currentObject) {
			const groupKey = String(currentObject[key]);

			if (!result[groupKey]) {
				result[groupKey] = [];
			}

			const { [key]: _, ...rest } = currentObject; // eslint-disable-line no-unused-vars
			result[groupKey].push(rest);

			return result;
		},
		/** @type {Record<string, Array<Omit<T, typeof key>>>} */ ({})
	);
}

/**
 * Async map function that supports sequential and parallel execution.
 * @template T, R
 * @param {Array<T>} array - The array to map over.
 * @param {(item: T, index: number, array: Array<T>) => Promise<R>} callback - An async function to process each item.
 * @param {object} [options] - Options to control execution mode.
 * @param {boolean} [options.sequential] - Whether to run the map sequentially or in parallel.
 * @returns {Promise<Array<R>>} - A Promise that resolves to a new array with the processed results.
 */
export async function asyncMap(array, callback, options = { sequential: false }) {
	if (options.sequential) {
		const results = [];
		for (let i = 0; i < array.length; i++) {
			results.push(await callback(array[i], i, array));
		}
		return results;
	} else {
		return Promise.all(array.map(callback));
	}
}

/**
 * Waterfall function to run tasks in sequence
 * @param {Array<Function>} tasks - An array of async functions that return a promise.
 * @param {*} initialValue - The initial value to be passed to the first task.
 * @returns {Promise<*>} - A promise that resolves with the final result.
 */
export async function waterfall(tasks, initialValue) {
	let result = initialValue;

	for (const task of tasks) {
		result = await task(result, initialValue);
	}

	return result;
}

/**
 * Checks if an object is empty or non-existing.
 * @param {object} obj - The object to check.
 * @returns {boolean} - True if the object is empty, false otherwise.
 */
export function isEmptyObject(obj) {
	if (!obj || typeof obj !== 'object') {
		return true;
	}
	return Object.entries(obj).length === 0 && obj.constructor === Object;
}

/**
 * Checks if an array exists and is non-empty
 * @param {Array<any>} arr - The object to check.
 * @returns {boolean} - True if the object is empty, false otherwise.
 */
export function isValidArray(arr) {
	return Array.isArray(arr) && arr.length > 0;
}

/**
 *
 * @param {number} timeInMilliseconds - time in millisecond for the length of delay
 * @returns {Promise<void>}
 */
export async function delay(timeInMilliseconds) {
	return new Promise((resolve) => setTimeout(resolve, timeInMilliseconds));
}
